import maplibregl from 'maplibre-gl'
import type { VehiclePositionWithDescriptors, VehiclePositionData, TransitServiceType } from '../../types/api'

/** Per-service marker icon, badge colour, and human-readable name. */
const SERVICE_PRESENTATION: Record<
  TransitServiceType,
  { icon: string; bgColor: string; serviceName: string }
> = {
  'sydney-metro': { icon: '🚇', bgColor: 'bg-blue-500', serviceName: 'Sydney Metro' },
  'sydney-trains': { icon: '🚂', bgColor: 'bg-green-500', serviceName: 'Sydney Trains' },
  'light-rail-innerwest': { icon: '🚊', bgColor: 'bg-orange-500', serviceName: 'Inner West Light Rail' },
}

export class MarkerService {
  private map: maplibregl.Map | null = null

  setMap(map: maplibregl.Map) {
    this.map = map
  }

  clearVehicleMarkers() {
    if (!this.map) return
    
    const existingMarkers = document.querySelectorAll('.vehicle-marker')
    existingMarkers.forEach(marker => marker.remove())
  }

  addVehicleMarkers(vehicles: VehiclePositionWithDescriptors[], serviceType: TransitServiceType = 'sydney-metro') {
    if (!this.map || !vehicles.length) return

    this.clearVehicleMarkers()

    vehicles.forEach((vehicle) => {
      const position = vehicle.vehicle_position
      if (position.latitude && position.longitude && this.map) {
        this.createVehicleMarker(position, vehicle, serviceType)
      }
    })
  }

  private createVehicleMarker(position: VehiclePositionData, vehicle: VehiclePositionWithDescriptors, serviceType: TransitServiceType = 'sydney-metro') {
    if (!this.map) return

    const { icon, bgColor, serviceName } = SERVICE_PRESENTATION[serviceType]

    // Create vehicle marker element
    const markerEl = document.createElement('div')
    markerEl.className = 'vehicle-marker'
    markerEl.innerHTML = `
      <div class="${bgColor} text-white text-xs px-2 py-1 rounded-full shadow-lg border-2 border-white">
        ${icon}
      </div>
    `
    markerEl.style.cursor = 'pointer'

    // Vehicle model comes from the TFNSW vehicle descriptor(s); use the first
    // one that carries a non-empty model.
    const vehicleModel = vehicle.tfnsw_vehicle_descriptors
      .find((descriptor) => descriptor.vehicle_model)
      ?.vehicle_model

    // Create popup content
    const popup = new maplibregl.Popup({ offset: 25 }).setHTML(`
      <div class="p-2">
        <h3 class="font-bold text-sm">${serviceName} Vehicle</h3>
        <p class="text-xs text-gray-600">Vehicle Model: ${vehicleModel || 'N/A'}</p>
        <p class="text-xs text-gray-600">Occupancy Status: ${position.occupancy_status || 'N/A'}</p>
        <p class="text-xs text-gray-600">Route: ${position.route_id || 'N/A'}</p>
        <p class="text-xs text-gray-600">Speed: ${position.speed ? `${Math.round(position.speed)} km/h` : 'N/A'}</p>
        <p class="text-xs text-gray-600">Status: ${position.current_status || 'N/A'}</p>
      </div>
    `)

    // Add marker to map
    new maplibregl.Marker(markerEl)
      .setLngLat([position.longitude!, position.latitude!])
      .setPopup(popup)
      .addTo(this.map)
  }
}

export const markerService = new MarkerService()
